<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getLabel } from '@/utils/data-display-utils';

import LocaleSelect from '../LocaleSelect.vue';
import BaseButton from '../base/BaseButton.vue';

import { useCommunityStore } from '@/store/communityStore';
import { getUniqueId, useUserStore } from '@/store/userStore';
import { getTranslation } from '@/utils/data-display-utils';
import { authService } from '@/services/auth.service';
import { paths } from '@/utils/paths';
import { Locales } from '@/store/language.interface';
import { useCurrentLocale } from '@/plugins/i18n';
import { useLocaleChoice } from '@/composables/useLocaleChoice';

// EMITS
const emit = defineEmits<{
  (e: 'openLoginDialog'): void;
}>();

// STORE
const communityStore = useCommunityStore();
const userStore = useUserStore();

const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// CONST
const router = useRouter();
const currentLocale = useCurrentLocale();
const { chooseLocale } = useLocaleChoice();

// REFS
const isMenuOpen = ref<boolean>(false);

// COMPUTED
const displayedIcon = computed(() => {
  return isMenuOpen.value ? 'close' : 'menu';
});

const userName = computed(() => {
  if (!userStore.user?.id) return '';

  return userStore.getUserName || getUniqueId(userStore.user);
});

// FUNCTIONS
function logout() {
  router.push(paths.root);
  authService.logout();
  isMenuOpen.value = false;
}

function closeMenu() {
  isMenuOpen.value = false;
}
</script>

<template>
  <q-btn
    unelevated
    :aria-label="getLabel('aria-label.header.open-menu', props.languageEnabled)"
    aria-haspopup="menu"
    :aria-expanded="isMenuOpen"
  >
    <q-icon :name="displayedIcon" />
    <q-menu class="mobile-menu" v-model="isMenuOpen" @before-show="isMenuOpen = true" @before-hide="isMenuOpen = false">
      <div class="mobile-menu">
        <div class="mobile-menu__title">
          <div>{{ getTranslation(communityStore.community, 'title', props.languageEnabled) }}</div>
        </div>
        <q-separator />
        <div class="mobile-menu__lang">
          <div class="lang">{{ getLabel('language', props.languageEnabled) }}</div>
          <LocaleSelect
            v-if="props.languageEnabled.length > 1"
            :model-value="currentLocale"
            :language-enabled="props.languageEnabled"
            @update:model-value="chooseLocale"
          />
        </div>
        <q-separator />

        <div class="mobile-menu__about">
          <router-link class="mobile-menu__about--link" :to="paths.about" @click="isMenuOpen = false">
            {{ getLabel('about-us', props.languageEnabled) }}
          </router-link>
          <div class="about">
            {{
              getLabel('about.cocoriko-platform', props.languageEnabled, {
                named: {
                  client: getTranslation(communityStore.community, 'title', props.languageEnabled),
                },
              })
            }}
          </div>
        </div>
        <template v-if="userStore.isUserLogged">
          <q-separator />
          <div class="mobile-menu__account">
            <router-link class="mobile-menu__account--link" :to="paths.profile" @click="closeMenu">
              {{ userName }}
            </router-link>
            <div class="mobile-menu__account--label">
              {{ getLabel('account.my-profile', props.languageEnabled) }}
            </div>
          </div>
        </template>
        <div class="mobile-menu__logout">
          <BaseButton
            v-if="userStore.isUserLogged"
            :text="getLabel('account.logout', props.languageEnabled)"
            color="secondary"
            left-icon="logout"
            full-width
            class="logout-btn"
            @click="logout"
          />
          <BaseButton
            v-else
            :text="getLabel('account.login', props.languageEnabled)"
            color="secondary"
            left-icon="login"
            full-width
            class="logout-btn"
            @click="emit('openLoginDialog')"
          />
        </div>
      </div>
    </q-menu>
  </q-btn>
</template>

<style lang="scss">
.mobile-menu {
  display: flex;
  flex-direction: column;
  font-size: $body-text;
  height: 100%;

  &__title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    font-size: $medium-title;
    font-weight: bold;
  }

  &__lang {
    display: flex;
    flex-direction: column;

    .lang {
      font-weight: bold;
    }
  }

  &__about {
    &-title {
      font-weight: bold;
      margin: $space-sm 0;
    }

    &--link {
      display: block;
      font-weight: bold;
      color: $color-neutral-black;
      text-decoration: none;
      margin-bottom: $space-xs;

      &:hover {
        color: $color-primary;
      }

      &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
        border-radius: 2px;
      }
    }
  }

  &__logout {
    display: flex;
    width: inherit;
    margin-top: auto;
    padding-top: $space-md;

    .logout-btn {
      .q-icon {
        margin-right: $space-sm;
      }
    }
  }

  &__account {
    display: flex;
    flex-direction: column;
    gap: $space-xs;

    &--link {
      display: inline-block;
      width: fit-content;
      font-weight: bold;
      color: $color-neutral-black;
      text-decoration: none;

      &:hover {
        color: $color-primary;
      }

      &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
        border-radius: 2px;
      }
    }

    &--label {
      color: $color-neutral-grey-400;
    }
  }

  &.q-position-engine {
    box-sizing: border-box;
    right: 0 !important;
    left: auto !important;
    top: var(--header-height) !important;
    height: calc(100vh - var(--header-height));
    max-height: calc(100vh - var(--header-height));
    width: 85vw;
    padding: 32px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .q-separator {
    margin: $space-sm 0;
  }

  &.q-menu {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
}
</style>
